/**
 * Public host hook (PEL-515) — turns engine state into a fully-resolved
 * `ActiveFtueStep` for the renderer (PEL-516).
 *
 * Resolves the active step's anchor element reactively (re-renders as anchors
 * mount/unmount), computes the step counter within its phase, and derives the
 * effective `surface`:
 *  - timeout fallback  -> the step's `fallback.resolveAs`
 *  - anchor missing    -> off-page fallback (if defined) else `none` (wait)
 *  - otherwise         -> the step's declared presentation
 */

import { useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAnchor, getElementLayer, subscribeAnchors } from './anchor-registry'
import { useFtueStore } from './ftue.store'
import { getFlowSteps, getStep } from './registry'
import type { ActiveFtueStep, FtueFallbackReason, FtuePresentation } from './types'

export function useFtue(): ActiveFtueStep | null {
  const navigate = useNavigate()
  const activeFlow = useFtueStore((s) => s.activeFlow)
  const activeStepId = useFtueStore((s) => s.activeStepId)
  const timedOut = useFtueStore((s) => s.timedOut)
  const complete = useFtueStore((s) => s.complete)
  const dismiss = useFtueStore((s) => s.dismiss)
  const skipAll = useFtueStore((s) => s.skipAll)
  const next = useFtueStore((s) => s.next)
  const back = useFtueStore((s) => s.back)

  const step = getStep(activeFlow, activeStepId)

  // Reactive anchor resolution — re-renders when the target mounts/unmounts.
  const element = useSyncExternalStore(subscribeAnchors, () => getAnchor(step?.anchorId))

  if (!step) return null

  // Step counter: position within the step's phase (or the whole flow).
  const steps = getFlowSteps(activeFlow)
  const peers = steps.filter(
    (candidate) =>
      (!step.phase || candidate.phase === step.phase) &&
      (!candidate.condition || candidate.condition()),
  )
  const index = Math.max(
    0,
    peers.findIndex((s) => s.id === step.id),
  )
  const total = peers.length

  // Effective surface.
  let surface: FtuePresentation | 'none' = step.presentation
  let fallbackReason: FtueFallbackReason = null

  if (timedOut && step.fallback && step.fallback.resolveAs !== 'skip') {
    surface = step.fallback.resolveAs
    fallbackReason = 'timeout'
  } else if ((step.presentation === 'coachmark' || step.presentation === 'badge') && !element) {
    // Anchored surface but the target isn't on screen.
    if (step.fallback && step.fallback.resolveAs !== 'skip') {
      surface = step.fallback.resolveAs
      fallbackReason = 'off-page'
    } else {
      surface = 'none' // wait for the anchor to appear
    }
  }

  return {
    step,
    surface,
    element,
    layer: getElementLayer(element),
    index,
    total,
    fallbackReason,
    onComplete: () => complete(step.id),
    onDismiss: () => dismiss(step.id),
    onSkipAll: () => {
      if (activeFlow) skipAll(activeFlow)
    },
    onNext: next,
    onBack: back,
    onCtaNavigate: (href: string) => {
      complete(step.id)
      navigate(href)
    },
  }
}
